import { Injectable, NgZone, inject } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { App } from '@capacitor/app';
import { Capacitor, type PluginListenerHandle } from '@capacitor/core';

const EXIT_MODAL_ID = 'selfwish-app-exit-modal';
const EXIT_MODAL_BACKDROP_ID = 'selfwish-app-exit-modal-backdrop';
const APP_LOGO_PATH = 'assets/images-nct/app-logo.svg';

/** Routes where hardware back should prompt to exit the app (not navigate away). */
const EXIT_ROOT_PATHS = new Set([
  '/',
  '/home',
  '/signin',
  '/signup',
]);

type ExitModalAction = 'exit' | 'stay';

@Injectable({
  providedIn: 'root',
})
export class AppExitService {
  private readonly zone = inject(NgZone);
  private readonly router = inject(Router);
  private readonly location = inject(Location);

  private backListener?: PluginListenerHandle;
  private exitDialogOpen = false;

  init(): void {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') {
      return;
    }

    void this.registerBackListener();
  }

  async destroy(): Promise<void> {
    await this.backListener?.remove();
    this.backListener = undefined;
    this.removeExitModal();
  }

  private async registerBackListener(): Promise<void> {
    this.backListener = await App.addListener('backButton', () => {
      this.zone.run(() => void this.handleBackButton());
    });
  }

  private async handleBackButton(): Promise<void> {
    const path = this.normalizePath(this.router.url);

    // On home / landing / auth entry: always confirm exit (do not use browser
    // history — visiting other pages leaves entries so back would skip the popup).
    if (EXIT_ROOT_PATHS.has(path)) {
      await this.promptExit();
      return;
    }

    if (window.history.length > 1) {
      this.location.back();
      return;
    }

    void this.router.navigate(['/home']);
  }

  private normalizePath(url: string): string {
    const path = url.split('?')[0].split('#')[0];
    if (!path || path === '') {
      return '/';
    }
    return path.endsWith('/') && path.length > 1 ? path.slice(0, -1) : path;
  }

  private async promptExit(): Promise<void> {
    if (this.exitDialogOpen) {
      return;
    }

    this.exitDialogOpen = true;

    try {
      const action = await this.showThemedExitModal();
      if (action === 'exit') {
        await App.exitApp();
      }
    } finally {
      this.exitDialogOpen = false;
    }
  }

  private buildExitModalHtml(): string {
    return (
      '<div class="modal fade selfwish-app-exit-modal show" id="' +
      EXIT_MODAL_ID +
      '" tabindex="-1" aria-modal="true" role="dialog" aria-labelledby="selfwishExitTitle">' +
      '<div class="modal-dialog modal-dialog-centered">' +
      '<div class="modal-content">' +
      '<div class="exit-modal-header">' +
      '<div class="exit-modal-logo">' +
      '<img src="' +
      APP_LOGO_PATH +
      '" alt="Selfwish" width="44" height="44" />' +
      '</div>' +
      '<h2 class="exit-modal-title" id="selfwishExitTitle">Leave Selfwish?</h2>' +
      '<p class="exit-modal-subtitle">You can come back anytime</p>' +
      '</div>' +
      '<div class="exit-modal-body">' +
      '<div class="exit-modal-icon-wrap" aria-hidden="true">' +
      '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
      '<polyline points="16 17 21 12 16 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
      '<line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>' +
      '</div>' +
      '<p class="exit-modal-message">Are you sure you want to close the app? Any unsaved progress may be lost.</p>' +
      '</div>' +
      '<div class="exit-modal-footer">' +
      '<button type="button" class="btn exit-btn-muted selfwish-exit-confirm">Exit app</button>' +
      '<button type="button" class="btn main-btn selfwish-exit-stay">Stay</button>' +
      '</div>' +
      '</div>' +
      '</div>' +
      '</div>'
    );
  }

  private showThemedExitModal(): Promise<ExitModalAction> {
    return new Promise((resolve) => {
      if (typeof document === 'undefined') {
        resolve('stay');
        return;
      }

      this.removeExitModal();
      document.body.insertAdjacentHTML('beforeend', this.buildExitModalHtml());

      const backdrop = document.createElement('div');
      backdrop.id = EXIT_MODAL_BACKDROP_ID;
      backdrop.className = 'modal-backdrop fade show';
      document.body.appendChild(backdrop);
      document.body.classList.add('modal-open');

      const finish = (action: ExitModalAction) => {
        this.removeExitModal();
        resolve(action);
      };

      const modal = document.getElementById(EXIT_MODAL_ID);
      if (!modal) {
        finish('stay');
        return;
      }

      modal.querySelector('.selfwish-exit-stay')?.addEventListener('click', () => {
        finish('stay');
      });

      modal.querySelector('.selfwish-exit-confirm')?.addEventListener('click', () => {
        finish('exit');
      });

      backdrop.addEventListener('click', () => finish('stay'));

      const stayBtn = modal.querySelector('.selfwish-exit-stay') as HTMLButtonElement | null;
      stayBtn?.focus();
    });
  }

  private removeExitModal(): void {
    if (typeof document === 'undefined') {
      return;
    }
    document.getElementById(EXIT_MODAL_ID)?.remove();
    document.getElementById(EXIT_MODAL_BACKDROP_ID)?.remove();
    document.body.classList.remove('modal-open');
  }
}
